import { memo, useCallback, useEffect, useRef } from 'react';
import { useMedia } from '../../media.context';
import {
  ThumbnailsGrid,
  ThumbnailWrapper,
  ThumbnailOverlay,
} from '../../media.styled';
import { AspectRatio } from '../../media.types';
import { Button, HStack, SVG, Text } from '../../..';
import { Icons } from '../../../icons';
import { MediaFile } from '../media-file/media-file';
import { Previewer } from '../previewer';

type ThumbnailsProps = {
  aspectRatio?: AspectRatio;
  columns?: number;
  emptyText?: string;
  withPreviewer?: boolean;
  viewMode?: 'grid' | 'carousel';
};

export const Thumbnails = memo(
  ({
    aspectRatio = '1:1',
    columns = 4,
    emptyText,
    withPreviewer = true,
    viewMode = 'grid',
  }: ThumbnailsProps) => {
    const carouselRef = useRef<HTMLDivElement>(null);

    const {
      items,
      removeItem,
      openPreview,
      canDelete,
      registerCarouselScroll,
      setCarouselScrollState,
    } = useMedia();

    const updateScrollState = useCallback(() => {
      const el = carouselRef.current;

      if (!el) {
        return;
      }

      setCarouselScrollState(
        el.scrollLeft > 0,
        el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      );
    }, [setCarouselScrollState]);

    useEffect(() => {
      if (viewMode !== 'carousel') {
        return;
      }

      registerCarouselScroll(
        () => {
          const el = carouselRef.current;

          if (!el) {
            return;
          }

          const itemWidth =
            (el.firstElementChild as HTMLElement)?.offsetWidth ?? 180;
          const gap = parseFloat(getComputedStyle(el).gap) || 0;
          el.scrollBy({ left: -(itemWidth + gap), behavior: 'smooth' });
        },
        () => {
          const el = carouselRef.current;

          if (!el) {
            return;
          }

          const itemWidth =
            (el.firstElementChild as HTMLElement)?.offsetWidth ?? 180;
          const gap = parseFloat(getComputedStyle(el).gap) || 0;
          el.scrollBy({ left: itemWidth + gap, behavior: 'smooth' });
        }
      );

      updateScrollState();
    }, [viewMode, registerCarouselScroll, updateScrollState]);

    // Пересчитываем при добавлении/удалении файлов
    useEffect(() => {
      if (viewMode !== 'carousel') {
        return;
      }

      updateScrollState();
    }, [items, viewMode, updateScrollState]);

    useEffect(() => {
      if (viewMode !== 'carousel') {
        return;
      }

      const el = carouselRef.current;

      if (!el) {
        return;
      }

      const observer = new ResizeObserver(updateScrollState);
      observer.observe(el);

      return () => observer.disconnect();
    }, [viewMode, updateScrollState]);

    if (!items.length && emptyText) {
      return <Text color="gray.500">{emptyText}</Text>;
    }

    return (
      <>
        {viewMode === 'grid' ? (
          <ThumbnailsGrid $columns={columns}>
            {items.map((item, index) => (
              <ThumbnailWrapper
                key={item.id}
                $ratio={aspectRatio}
                onClick={() => openPreview(index)}
              >
                <MediaFile file={item} />

                {canDelete && (
                  <ThumbnailOverlay>
                    <Button
                      iconButton={<SVG src={Icons.Trash} />}
                      mode="neutral"
                      size="s"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(item.id);
                      }}
                    />
                  </ThumbnailOverlay>
                )}
              </ThumbnailWrapper>
            ))}
          </ThumbnailsGrid>
        ) : (
          <HStack
            ref={carouselRef}
            onScroll={updateScrollState}
            style={{ overflow: 'auto hidden', scrollbarWidth: 'none' }}
          >
            {items.map((item, index) => (
              <ThumbnailWrapper
                key={item.id}
                $ratio={aspectRatio}
                onClick={() => openPreview(index)}
                style={{
                  flexBasis: 0,
                  minWidth: 180,
                }}
              >
                <MediaFile file={item} />

                {canDelete && (
                  <ThumbnailOverlay>
                    <Button
                      iconButton={<SVG src={Icons.Trash} />}
                      mode="neutral"
                      size="s"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(item.id);
                      }}
                    />
                  </ThumbnailOverlay>
                )}
              </ThumbnailWrapper>
            ))}
          </HStack>
        )}

        {withPreviewer && <Previewer />}
      </>
    );
  }
);
